import { buildProjectJoinUrl, mapShareError } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Text, XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { getEnvVar } from '@/env';
import { useMyTeamQuery } from '@/features/team/team.query';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import {
  useAddMember,
  useProjectMembers,
  useShareLink,
} from '../project-members.query';

import { getOtherMembers, isProjectPrivate } from './project-visibility';

import type { Project } from '@littlebird/sdk';

const DISMISSAL_KEY = 'project-share-card-dismissed';

type ProjectShareCardProps = {
  project: Project;
  onOpenShare: () => void;
};

export const ProjectShareCard = observer(
  ({ project, onOpenShare }: ProjectShareCardProps) => {
    const { canInvite, canManageLink } = project.capabilities;
    const [isDismissed, setIsDismissed] = useState(
      () => window.localStorage.getItem(DISMISSAL_KEY) === 'true'
    );
    const { data: members, isLoading } = useProjectMembers(
      project.id,
      canInvite
    );
    const others = getOtherMembers(members, userStore.userId);
    const isPrivate = isProjectPrivate(project, others);
    const { data: teamDashboard } = useMyTeamQuery(
      canInvite && !isLoading && Boolean(members) && isPrivate && !isDismissed,
      { staleTime: 60_000 }
    );
    const team = teamDashboard?.team;
    const addMember = useAddMember(project.id);
    const shouldShow =
      canInvite &&
      !isLoading &&
      Boolean(members) &&
      isPrivate &&
      team !== null &&
      team !== undefined &&
      !isDismissed;
    const shareLink = useShareLink(
      project.id,
      shouldShow && canManageLink,
      canManageLink
    );

    if (!shouldShow || !team) return null;

    const handleShareWithTeam = async () => {
      try {
        await addMember.mutateAsync({ teamId: team.id, role: 'member' });
        trackEvent('PROJECT_SHARE_COMPLETED', {
          role: 'member',
          target: 'team',
        });
        toast.success(`Shared with ${team.name}`);
      } catch (error) {
        toast.error(mapShareError(error));
      }
    };

    const handleCopyLink = async () => {
      const existing = shareLink.token;
      const token = existing ?? (await shareLink.mint());
      if (!token) return;

      const linkUrl = buildProjectJoinUrl(
        token,
        getEnvVar('BASE_URL_FOR_PUBLIC_LINKS') ?? window.location.origin
      );
      try {
        await navigator.clipboard.writeText(linkUrl);
        toast.success('Link copied');
      } catch (error) {
        if (!existing) await shareLink.revoke();
        toast.error(mapShareError(error));
      }
    };

    return (
      <YStack className="gap-3 rounded-lg border border-border bg-background-subtle px-4 py-3">
        <XStack className="items-start gap-3">
          <Icons.lock className="mt-1 size-4 shrink-0 text-foreground-muted" />
          <YStack className="min-w-0 gap-1">
            <Text size="sm" fontWeight="medium" as="span">
              Only you can see this project
            </Text>
            <Text size="xs" variant="muted" as="span">
              Share it so your team can find its chats, notes and files.
            </Text>
          </YStack>
        </XStack>
        <XStack className="flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={() => void handleShareWithTeam()}
            disabled={addMember.isPending}
          >
            Share with {team.name}
            {teamDashboard?.members && (
              <span className="text-accent-foreground-subtle">
                {' · '}
                {teamDashboard.members.length}
              </span>
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={onOpenShare}>
            <Icons.search className="size-3.5" />
            Add people by name or email
          </Button>
          {canManageLink && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void handleCopyLink()}
              disabled={shareLink.isMinting}
            >
              <Icons.link className="size-3.5" />
              Copy link
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={() => {
              window.localStorage.setItem(DISMISSAL_KEY, 'true');
              setIsDismissed(true);
            }}
          >
            Not now
          </Button>
        </XStack>
      </YStack>
    );
  }
);
